import { useState } from "react";
import { useMutation } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CloseButton,
  Dialog,
  Heading,
  HStack,
  IconButton,
  Portal,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LuPlus, LuX } from "react-icons/lu";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useQuery } from "@/lib/convex";
import { useViewerAccess } from "@/lib/access";
import { readableError } from "@/lib/errors";
import { PageHeader } from "../components/PageHeader";
import { LoadingCard } from "../components/LoadingCard";
import { SinglePersonPicker } from "../components/SinglePersonPicker";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { Tooltip } from "../components/ui/tooltip";

type Overview = FunctionReturnType<typeof api.roles.overview>;
type RoleRow = Overview["roles"][number];
type Holder = RoleRow["holders"][number];

const breadcrumbs = [
  { label: "CeMMbase", to: "/" },
  { label: "Administration", to: "/admin" },
  { label: "Roles" },
];

// What each role opens, in the words of the pages it opens. Kept with the
// page rather than the backend: it is a description for the person choosing,
// and it changes when a page does.
const ROLE_TEXT: Record<RoleRow["role"], { title: string; description: string }> = {
  sysAdmin: {
    title: "System admin",
    description: "The audit log, reconciliation, leave, the Convex dashboard and this page. Can look at CeMMbase as somebody else, and signs off anything the HR, grants or activity admins sign.",
  },
  activityAdmin: {
    title: "Activity admin",
    description: "The activity catalogue, assigning activities, programme progress, seminar planning and leave. Can open and correct anybody's activities.",
  },
  hrAdmin: {
    title: "HR admin",
    description: "Reconciliation and leave, and signs off steps that name the HR admins.",
  },
  grantsAdmin: {
    title: "Grants admin",
    description: "Can read anybody's activities and programme record, and signs off steps that name the grants admins.",
  },
};

const dateFormat = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

/**
 * Administration > Roles: who holds each admin role, and changing it.
 *
 * A card per role rather than a role column on the people table: there are a
 * handful of admins among hundreds of people, and the question asked is "who
 * are the activity admins", not "what is this person". Student and faculty are
 * not here -- the directory says who those are.
 */
export function RolesPage() {
  const access = useViewerAccess();
  const overview = useQuery(api.roles.overview, access?.isSysAdmin ? {} : "skip");

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Roles"
        description="Who administers what in CeMMbase. Student and faculty come from the directory and are not set here."
        breadcrumbs={breadcrumbs}
      />
      <Stack gap={6} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {access === undefined || (access.isSysAdmin && overview === undefined) ? (
          <LoadingCard label="Loading roles…" />
        ) : !access.isSysAdmin || overview === undefined ? (
          <Text fontSize="sm" color="fg.muted">This page is for system administrators.</Text>
        ) : (
          <>
            {overview.seeded ? null : (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Description>
                  These are still the roles from the configuration file. They are copied here at the
                  next directory sync, or with the first change made on this page.
                </Alert.Description>
              </Alert.Root>
            )}
            <SimpleGrid columns={{ base: 1, xl: 2 }} gap={4}>
              {overview.roles.map((row) => (
                <RoleCard key={row.role} row={row} candidates={overview.candidates} />
              ))}
            </SimpleGrid>
          </>
        )}
      </Stack>
    </Box>
  );
}

function RoleCard({ row, candidates }: { row: RoleRow; candidates: Overview["candidates"] }) {
  const grant = useMutation(api.roles.grant);
  const revoke = useMutation(api.roles.revoke);
  const [chosen, setChosen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Holder | null>(null);
  const [busy, setBusy] = useState(false);
  const text = ROLE_TEXT[row.role];
  const holderIds = new Set(row.holders.map((holder) => String(holder.personId)));
  const options = candidates.filter((candidate) => !holderIds.has(String(candidate._id)));

  async function add() {
    if (chosen === null) return;
    setAdding(true);
    try {
      await grant({ personId: chosen as Id<"persons">, role: row.role });
      toastSuccess(`${options.find((option) => String(option._id) === chosen)?.fullName ?? "They"} is now ${text.title.toLowerCase()}`);
      setChosen(null);
    } catch (error) {
      const { message, detail } = readableError(error, "The role could not be given");
      toastError(message, detail);
    } finally {
      setAdding(false);
    }
  }

  async function remove(holder: Holder) {
    setBusy(true);
    try {
      await revoke({ personId: holder.personId, role: row.role });
      toastSuccess(`${holder.fullName} is no longer ${text.title.toLowerCase()}`);
      setRemoving(null);
    } catch (error) {
      const { message, detail } = readableError(error, "The role could not be taken away");
      toastError(message, detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={4} p={6}>
        <Stack gap={1}>
          <HStack justify="space-between" align="baseline">
            <Heading size="md">{text.title}</Heading>
            <Text fontSize="sm" color="fg.muted">
              {row.holders.length} {row.holders.length === 1 ? "person" : "people"}
            </Text>
          </HStack>
          <Text fontSize="sm" color="fg.muted">{text.description}</Text>
        </Stack>

        <Stack gap={0} borderTopWidth="1px" borderColor="border">
          {row.holders.length === 0 ? (
            <Text py={3} fontSize="sm" color="fg.muted">Nobody holds this role.</Text>
          ) : row.holders.map((holder) => (
            <HStack key={holder.personId} justify="space-between" gap={3} py={2.5} borderBottomWidth="1px" borderColor="border">
              <Stack gap={0} minW={0}>
                <Text fontSize="sm" fontWeight="medium">
                  {holder.fullName}{holder.isViewer ? " (you)" : ""}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  Since {dateFormat.format(holder.grantedAt)}
                  {holder.grantedByName === null ? ", from the configuration" : `, by ${holder.grantedByName}`}
                </Text>
              </Stack>
              <Tooltip content={`Take away ${text.title.toLowerCase()}`}>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label={`Take ${text.title.toLowerCase()} away from ${holder.fullName}`}
                  onClick={() => setRemoving(holder)}
                >
                  <LuX />
                </IconButton>
              </Tooltip>
            </HStack>
          ))}
        </Stack>

        <HStack align="end" gap={2}>
          <Box flex="1" minW={0}>
            <SinglePersonPicker
              label={`Make somebody ${text.title.toLowerCase()}`}
              people={options}
              selectedPersonId={chosen}
              onChange={setChosen}
            />
          </Box>
          <Button size="sm" colorPalette="blue" disabled={chosen === null} loading={adding} onClick={() => void add()}>
            <LuPlus />
            Add
          </Button>
        </HStack>
      </Card.Body>

      {/* Asked first: taking a role away closes pages to somebody at once,
          and taking your own away closes this one. */}
      <Dialog.Root role="alertdialog" open={removing !== null} onOpenChange={(event) => { if (!event.open) setRemoving(null); }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="md">
              <Dialog.Header>
                <Dialog.Title>Take away {text.title.toLowerCase()}?</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <Text fontSize="sm">
                  {removing?.isViewer
                    ? `You will no longer be ${text.title.toLowerCase()}, from now on.`
                      + (row.role === "sysAdmin" ? " This page will close to you." : "")
                    : `${removing?.fullName} will no longer be ${text.title.toLowerCase()}, from now on.`}
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <ButtonGroup gap={2}>
                  <Button variant="ghost" onClick={() => setRemoving(null)}>Cancel</Button>
                  <Button colorPalette="red" loading={busy} onClick={() => removing && void remove(removing)}>
                    Take it away
                  </Button>
                </ButtonGroup>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Card.Root>
  );
}
